/* Payroll, GOSI, Art. 92, EOSB, settlement and leave entitlement.
 * Mirrors api/internal/domain/payroll.go — both are checked against
 * contracts/test-vectors/domain.json. Keep operation order identical. */
import type {
  Adjustment, Employee, EosbResult, GosiBreakdown, GosiSettings, Loan, MudadIssue,
  Payslip, PayrollTotals, Settlement,
} from "../data/types";
import { yearsBetween } from "./dates";
import { bandMonths, countryOf, ladder, wageBasis } from "./country";

/** Round half away from zero to 2 dp (matches Go math.Round). */
export function round2(x: number): number {
  const s = x < 0 ? -1 : 1;
  return (s * Math.round(Math.abs(x) * 100)) / 100;
}

export const ZERO_ADJ: Adjustment = { ot: 0, bonus: 0, absDays: 0, ded: 0 };

/** New-regime Saudi annuity rate: 9% + 0.5pt on each 1 July 2025..2028 passed. */
export function annuityRateNew(today: string, base = 9): number {
  let steps = 0;
  for (const y of [2025, 2026, 2027, 2028]) if (today >= y + "-07-01") steps++;
  return base + 0.5 * steps;
}

/** Social insurance of one employee. Saudi Arabia: GOSI (legacy / new regime). Other GCC countries: citizens pay the
 * country's pension rates (regime "legacy"), non-citizens only the non-citizen rates (regime "non-saudi" = non-citizen). */
export function gosiOf(e: Employee, g: GosiSettings, today: string, country = "SA"): GosiBreakdown {
  if (country !== "SA") return siOf(e, g, today, country);
  const base = Math.min(g.cap, e.basic + e.housing);
  if (e.nationality === "SA" && e.gosiRegime === "new") {
    const a = annuityRateNew(today, g.saEmpAnn);
    return {
      base, regime: "new", annRate: a,
      empAnn: (base * a) / 100, empSaned: (base * g.saEmpSaned) / 100,
      erAnn: (base * a) / 100, erSaned: (base * g.saErSaned) / 100, erOH: (base * g.saErOH) / 100,
      emp: (base * (a + g.saEmpSaned)) / 100, er: (base * (a + g.saErSaned + g.saErOH)) / 100,
    };
  }
  if (e.nationality === "SA") {
    return {
      base, regime: "legacy", annRate: g.saEmpAnn,
      empAnn: (base * g.saEmpAnn) / 100, empSaned: (base * g.saEmpSaned) / 100,
      erAnn: (base * g.saErAnn) / 100, erSaned: (base * g.saErSaned) / 100, erOH: (base * g.saErOH) / 100,
      emp: (base * (g.saEmpAnn + g.saEmpSaned)) / 100, er: (base * (g.saErAnn + g.saErSaned + g.saErOH)) / 100,
    };
  }
  return { base, regime: "non-saudi", annRate: 0, empAnn: 0, empSaned: 0, erAnn: 0, erSaned: 0, erOH: (base * g.nsErOH) / 100, emp: 0, er: (base * g.nsErOH) / 100 };
}

function siOf(e: Employee, g: GosiSettings, today: string, country: string): GosiBreakdown {
  const c = countryOf(country);
  const base = Math.min(g.cap, wageBasis(e, c.si.base));
  if (e.nationality === c.code) {
    return {
      base, regime: "legacy", annRate: g.saEmpAnn,
      empAnn: (base * g.saEmpAnn) / 100, empSaned: (base * g.saEmpSaned) / 100,
      erAnn: (base * g.saErAnn) / 100, erSaned: (base * g.saErSaned) / 100, erOH: (base * g.saErOH) / 100,
      emp: (base * (g.saEmpAnn + g.saEmpSaned)) / 100, er: (base * (g.saErAnn + g.saErSaned + g.saErOH)) / 100,
    };
  }
  const fund = c.si.expatEosbFund && today >= c.si.expatEosbFund.from ? ladder(yearsBetween(e.joined, today), c.si.expatEosbFund.rates) : 0;
  const nsEmp = g.nsEmp ?? 0;
  const erRate = g.nsErOH + fund;
  return { base, regime: "non-saudi", annRate: 0, empAnn: 0, empSaned: (base * nsEmp) / 100, erAnn: 0, erSaned: 0, erOH: (base * erRate) / 100, emp: (base * nsEmp) / 100, er: (base * erRate) / 100 };
}

export const hourlyRate = (e: Employee) => e.basic / 30 / 8;
export const wageOf = (e: Employee) => e.basic + e.housing + e.transport + e.other;

/** Deterministic demo adjustments used when the run has no saved adjustment. */
export function defaultAdj(e: Employee): Adjustment {
  const i = e.id;
  return {
    ot: e.dept === "wh" || e.dept === "ops" ? (i % 4) * 4 + (i % 3 === 0 ? 6 : 0) : 0,
    bonus: i === 123 ? 3000 : i === 111 ? 1500 : 0,
    absDays: i === 118 ? 2 : 0,
    ded: i === 132 ? 250 : 0,
  };
}

/** Monthly instalment of the employee's first loan in "Repaying" status. */
export function loanDeduction(employeeId: number, loans: Loan[]): number {
  const l = loans.find((x) => x.employeeId === employeeId && x.status === "Repaying");
  return l && l.months > 0 ? Math.round(l.amount / l.months) : 0;
}

export function validateAdjustment(a: Partial<Adjustment>): Record<string, string> {
  const err: Record<string, string> = {};
  for (const k of ["ot", "bonus", "absDays", "ded"] as const) {
    const v = a[k];
    if (v == null) continue;
    if (typeof v !== "number" || !isFinite(v)) err[k] = "must be a number";
    else if (v < 0) err[k] = "must be ≥ 0";
  }
  if (a.absDays != null && a.absDays > 31) err.absDays = "max 31";
  if (a.ot != null && a.ot > 240) err.ot = "max 240 hours";
  return err;
}

export function payslip(e: Employee, adj: Adjustment, loans: Loan[], g: GosiSettings, today: string, period: string, country = "SA"): Payslip {
  const gs = gosiOf(e, g, today, country);
  const otPay = adj.ot * hourlyRate(e) * countryOf(country).overtime;
  const absAmt = adj.absDays * ((e.basic + e.housing + e.transport) / 30);
  const loan = loanDeduction(e.id, loans);
  const earnings = e.basic + e.housing + e.transport + e.other + otPay + adj.bonus;
  const deductions = gs.emp + absAmt + adj.ded + loan;
  const limit = wageOf(e) * 0.5;
  const nonGosi = absAmt + adj.ded + loan;
  return {
    employeeId: e.id, period,
    basic: e.basic, housing: e.housing, transport: e.transport, other: e.other,
    otHours: adj.ot, ot: round2(otPay), bonus: adj.bonus,
    absDays: adj.absDays, abs: round2(absAmt), ded: adj.ded, loan,
    gosi: roundGosi(gs),
    earnings: round2(earnings), deductions: round2(deductions), net: round2(earnings - deductions),
    cap: { limit: round2(limit), amount: round2(nonGosi), over: nonGosi > limit },
  };
}

function roundGosi(g: GosiBreakdown): GosiBreakdown {
  return { ...g, empAnn: round2(g.empAnn), empSaned: round2(g.empSaned), erAnn: round2(g.erAnn), erSaned: round2(g.erSaned), erOH: round2(g.erOH), emp: round2(g.emp), er: round2(g.er) };
}

export function payrollTotals(slips: Payslip[]): PayrollTotals {
  const t = { headcount: slips.length, gross: 0, deductions: 0, net: 0, gosiEmp: 0, gosiEr: 0, ot: 0, bonus: 0, employerCost: 0 };
  for (const s of slips) {
    t.gross += s.earnings; t.deductions += s.deductions; t.net += s.net;
    t.gosiEmp += s.gosi.emp; t.gosiEr += s.gosi.er; t.ot += s.ot; t.bonus += s.bonus;
  }
  t.employerCost = t.gross + t.gosiEr;
  for (const k of ["gross", "deductions", "net", "gosiEmp", "gosiEr", "ot", "bonus", "employerCost"] as const) t[k] = round2(t[k]);
  return t;
}

/** Mudad pre-check: GOSI-registered wage deviates > 20% from basic+housing, or Qiwa basic ≠ payroll basic. */
export function mudadIssues(emps: Employee[]): MudadIssue[] {
  const out: MudadIssue[] = [];
  for (const e of emps) {
    if (e.gosiWage > 0) {
      const d = Math.abs(e.basic + e.housing - e.gosiWage) / e.gosiWage;
      if (d > 0.2) out.push({ employeeId: e.id, kind: "GOSI", registered: e.gosiWage, payroll: e.basic + e.housing, deviation: round4(d) });
    }
    if (e.qiwa.basic !== e.basic) {
      const d2 = e.qiwa.basic > 0 ? Math.abs(e.basic - e.qiwa.basic) / e.qiwa.basic : 1;
      out.push({ employeeId: e.id, kind: "Qiwa", registered: e.qiwa.basic, payroll: e.basic, deviation: round4(d2) });
    }
  }
  return out;
}
const round4 = (x: number) => Math.round(x * 10000) / 10000;

/** End-of-service benefit. Saudi Arabia: Arts. 84–85; other GCC countries: the rule in contracts/countries.json.
 * reason: "termination" | "resignation" | other (treated as full). */
export function eosb(e: Employee, end: string, reason: string, wageOverride?: number, country = "SA"): EosbResult {
  if (country !== "SA") return eosbOf(e, end, reason, wageOverride, country);
  const wage = wageOverride || e.basic + e.housing + e.transport;
  const y = yearsBetween(e.joined, end);
  const first = (Math.min(y, 5) * wage) / 2;
  const rest = Math.max(0, y - 5) * wage;
  const full = first + rest;
  const f = reason === "resignation" ? (y < 2 ? 0 : y < 5 ? 1 / 3 : y < 10 ? 2 / 3 : 1) : 1;
  return { wage, years: round4(y), first: round2(first), rest: round2(rest), full: round2(full), factor: round4(f), award: round2(full * f) };
}

function eosbOf(e: Employee, end: string, reason: string, wageOverride: number | undefined, country: string): EosbResult {
  const c = countryOf(country), r = c.eosb;
  const wage = wageOverride || wageBasis(e, r.wage);
  const y = yearsBetween(e.joined, end);
  if ((!r.citizens && e.nationality === c.code) || y < r.minYears) return { wage, years: round4(y), first: 0, rest: 0, full: 0, factor: 0, award: 0 };
  const yPaid = r.serviceUntil && end > r.serviceUntil ? yearsBetween(e.joined, r.serviceUntil) : y;
  let first: number, full: number;
  if (r.legacy && e.joined < r.legacy.until) {
    const y1 = Math.min(yPaid, yearsBetween(e.joined, r.legacy.until));
    const [f1, t1] = bandMonths(y1, r.legacy.bands);
    const t2 = bandMonths(yPaid - y1, r.bands)[1];
    first = f1 * wage; full = (t1 + t2) * wage;
  } else {
    const [f, t] = bandMonths(yPaid, r.bands);
    first = f * wage; full = t * wage;
  }
  if (r.capMonths > 0) full = Math.min(full, r.capMonths * wage);
  first = Math.min(first, full);
  const f = reason === "resignation" ? ladder(y, r.resign) : 1;
  return { wage, years: round4(y), first: round2(first), rest: round2(full - first), full: round2(full), factor: round4(f), award: round2(full * f) };
}

export const annualEntitlement = (e: Employee, today: string, country = "SA") => {
  const [a, b] = countryOf(country).leave.annual;
  return yearsBetween(e.joined, today) >= 5 ? b : a;
};
/** Demo "days used" (deterministic). Real implementation sums approved annual leave. */
export const usedAnnualDemo = (e: Employee) => (e.id * 7) % 13;

export function leaveLiability(emps: Employee[], today: string, country = "SA"): number {
  return round2(emps.reduce((a, e) => a + ((annualEntitlement(e, today, country) - usedAnnualDemo(e)) * (e.basic + e.housing + e.transport)) / 30, 0));
}

/** Final settlement (salary to last day + leave encashment + EOSB + ticket − loan, loan capped at 50%). */
export function settlement(e: Employee, lastDay: string, type: string, loans: Loan[], today: string, country = "SA"): Settlement {
  const r = eosb(e, lastDay, type === "Resignation" ? "resignation" : "termination", undefined, country);
  const wage = wageOf(e), daily = wage / 30;
  const leaveDays = Math.max(0, annualEntitlement(e, today, country) - usedAnnualDemo(e));
  const leavePay = (leaveDays * (e.basic + e.housing + e.transport)) / 30;
  const workedDays = +lastDay.slice(8, 10);
  const salary = daily * Math.min(30, workedDays);
  const loan = loans.filter((l) => l.employeeId === e.id && l.status === "Repaying").reduce((a, l) => a + l.amount * (1 - l.paid / l.months), 0);
  const ticket = e.ticket ? e.ticket.value : 0;
  const gross = salary + leavePay + r.award + ticket;
  const cap = gross * 0.5;
  const ded = Math.min(loan, cap);
  return {
    salary: round2(salary), days: workedDays, leaveDays, leavePay: round2(leavePay), eosb: r.award, eo: r, ticket,
    loan: round2(loan), ded: round2(ded), capped: loan > cap, net: round2(gross - ded),
  };
}
